import { createClient } from '@supabase/supabase-js';
import type { Database } from '../../types/supabase';

// UUID generation polyfill for Safari
function generateUUID(): string {
  // Check if crypto.randomUUID is available
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

interface ConnectionState {
  isEnabled: boolean;
  lastConnection: string | null;
  connectionDuration: number | null; // in minutes
  autoDisconnect: boolean;
}

class ConnectionManager {
  private static instance: ConnectionManager;
  private supabase: ReturnType<typeof createClient<Database>> | null = null;
  private subscribers: Set<(isConnected: boolean) => void> = new Set();
  private connectionState: ConnectionState = {
    isEnabled: true, // Default to enabled
    lastConnection: null,
    connectionDuration: 60,
    autoDisconnect: false
  };
  private connectionCheckInterval: number | null = null;
  private readonly PAGE_SIZE = 20; // Number of scores per page
  private readonly CACHE_TTL = 30 * 1000; // 30 seconds cache instead of 5 minutes
  private readonly RECONNECT_INTERVAL = 5000; // 5 seconds
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  private constructor() {
    // Load saved state
    const savedState = localStorage.getItem('supabase_connection_state');
    if (savedState) {
      this.connectionState = JSON.parse(savedState);
    }

    // Debug environment variables
    console.log('Supabase URL:', import.meta.env.VITE_SUPABASE_URL);
    console.log('Supabase Key exists:', !!import.meta.env.VITE_SUPABASE_ANON_KEY);
    console.log('Environment:', import.meta.env.MODE);

    // Always auto-connect
    const supabaseUrl = this.normalizeSupabaseUrl(import.meta.env.VITE_SUPABASE_URL);
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (this.validateSupabaseCredentials(supabaseUrl, supabaseKey)) {
      this.connect();
    }

    this.startConnectionCheck();
  }

  private normalizeSupabaseUrl(url: string): string {
    if (!url) return '';
    
    try {
      // Ensure URL has protocol
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      // Remove trailing slash if present
      return url.replace(/\/$/, '');
    } catch (error) {
      console.error('Error normalizing Supabase URL:', error);
      return '';
    }
  }

  private validateSupabaseCredentials(url: string, key: string): boolean {
    if (!url || !key) {
      console.error('Supabase credentials missing');
      return false;
    }

    try {
      // Validate URL format
      new URL(url);
      
      // Basic key format validation
      if (!key.includes('.')) {
        console.error('Invalid Supabase key format');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Invalid Supabase URL format:', error);
      return false;
    }
  }

  static getInstance(): ConnectionManager {
    if (!ConnectionManager.instance) {
      ConnectionManager.instance = new ConnectionManager();
    }
    return ConnectionManager.instance;
  }

  subscribe(callback: (isConnected: boolean) => void): () => void {
    this.subscribers.add(callback);
    // Immediately notify subscriber of current state
    callback(this.isConnected());
    return () => this.subscribers.delete(callback);
  }

  private notifySubscribers(isConnected: boolean) {
    this.subscribers.forEach(callback => callback(isConnected));
  }

  private startConnectionCheck(): void {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }

    this.connectionCheckInterval = window.setInterval(async () => {
      if (this.supabase && this.connectionState.isEnabled) {
        try {
          const { error } = await this.supabase
            .from('tournaments')
            .select('id')
            .limit(1)
            .maybeSingle();

          if (error && !error.message.includes('No rows found')) {
            console.warn('Connection check failed, disconnecting...');
            this.disconnect();
          }
        } catch (error) {
          console.warn('Connection check failed, disconnecting...');
          this.disconnect();
        }
      }
    }, 30000); // Check every 30 seconds
  }

  private async attemptReconnect(): Promise<boolean> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('Max reconnection attempts reached');
      return false;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    const success = await this.connect();
    if (success) {
      this.reconnectAttempts = 0;
      return true;
    }

    // Schedule next attempt
    setTimeout(() => this.attemptReconnect(), this.RECONNECT_INTERVAL);
    return false;
  }

  public async connect(): Promise<boolean> {
    try {
      // If already connected, just return true
      if (this.supabase && this.isConnected()) {
        return true;
      }

      // Create new client if needed
      if (!this.supabase) {
        console.log('Creating new Supabase client...');
        this.supabase = createClient<Database>(
          import.meta.env.VITE_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_ANON_KEY,
          {
            auth: {
              persistSession: true,
              autoRefreshToken: true,
              detectSessionInUrl: true
            },
            realtime: {
              params: {
                eventsPerSecond: 20 // Increased from 10
              }
            }
          }
        );
      }

      // Test connection with a simple query
      console.log('Testing connection...');
      const { data, error } = await this.supabase.from('tournaments').select('count').limit(1);
      
      if (error) {
        console.error('Connection test failed:', error);
        this.notifySubscribers(false);
        return false;
      }

      console.log('Connection successful');
      // Update connection state
      this.connectionState.isEnabled = true;
      this.connectionState.lastConnection = new Date().toISOString();
      this.saveState();

      // Notify subscribers
      this.notifySubscribers(true);

      return true;
    } catch (error) {
      console.error('Error connecting to Supabase:', error);
      this.notifySubscribers(false);
      return false;
    }
  }

  disconnect(): void {
    if (this.supabase) {
      // Remove all channel subscriptions
      const channels = this.supabase.getChannels();
      channels.forEach(channel => {
        this.supabase?.removeChannel(channel);
      });
    }
    this.supabase = null;
    this.connectionState = {
      ...this.connectionState,
      isEnabled: false,
      lastConnection: null
    };
    this.saveState();
    this.notifySubscribers(false);
  }

  getClient() {
    return this.supabase;
  }

  public isConnected(): boolean {
    return this.connectionState.isEnabled && this.supabase !== null;
  }

  getConnectionState(): ConnectionState {
    return { ...this.connectionState };
  }

  setAutoDisconnect(minutes: number | null): void {
    this.connectionState.autoDisconnect = !!minutes;
    this.connectionState.connectionDuration = minutes;
    this.saveState();

    if (minutes) {
      setTimeout(() => {
        this.disconnect();
      }, minutes * 60 * 1000);
    }
  }

  private saveState() {
    localStorage.setItem('supabase_connection_state', JSON.stringify(this.connectionState));
  }
}

export const connectionManager = ConnectionManager.getInstance();